import React, { useState, useEffect, useMemo } from 'react';
import SEOHead from '../components/SEOHead';
import { useParams, Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { toast } from 'react-toastify';
import { ChevronLeftIcon, ChevronRightIcon, HomeIcon } from '@heroicons/react/24/outline';
import api from '../utils/api';
import { useImageSlider } from '../hooks/useImageSlider';
import { Lens } from '../components/ui/lens';
import { trackViewItem, trackAddToCart } from '../utils/analytics';
import ProductReviewSection from '../components/ProductReviewSection';

function ProductDetail() {
  const { id } = useParams();
  const { addToCart } = useCart();
  
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  
  // Kategori indirimi için state'ler
  const [categoryDiscount, setCategoryDiscount] = useState(null);
  const [categoryDiscountLoading, setCategoryDiscountLoading] = useState(false);
  
  // Flash Sale için state'ler
  const [flashSaleDiscount, setFlashSaleDiscount] = useState(null);
  const [flashSaleLoading, setFlashSaleLoading] = useState(false);
  const [cartCampaign, setCartCampaign] = useState(null);
  
  // Paket kampanyası için state'ler
  const [packageCampaign, setPackageCampaign] = useState(null);
  const [packageCampaignLoading, setPackageCampaignLoading] = useState(false);
  
  // Özel gün kampanyası için state'ler
  const [specialDayDiscount, setSpecialDayDiscount] = useState(null);
  const [specialDayLoading, setSpecialDayLoading] = useState(false);

  const uniqueImages = useMemo(() => {
    if (!product) return [];
    const allImages = [
      product.main_image_url,
      ...(product.gallery_images || []),
      ...(product.variants?.map(v => v.variant_image_url).filter(Boolean) || [])
    ];
    return [...new Set(allImages)].filter(Boolean);
  }, [product]);
  
  const { currentIndex, goToIndex, startSlider, stopSlider } = useImageSlider(uniqueImages, 3500);

  useEffect(() => {
    if (uniqueImages.length > 1) {
        startSlider();
    }
    return () => stopSlider();
  }, [uniqueImages, startSlider, stopSlider]);

  // Kategori indirimini kontrol et - YENİ BİRLEŞİK API
  const checkCategoryDiscount = async () => {
    if (!product || !product.id) {
      console.log('❌ ProductDetail: Ürün bilgisi eksik:', { product: !!product, id: product?.id });
      return;
    }
    
    console.log('🔍 ProductDetail: Yeni birleşik API ile kategori indirimi kontrol ediliyor:', {
      name: product.name,
      category: product.category,
      id: product.id,
      price: product.price
    });
    
    setCategoryDiscountLoading(true);
    try {
      const requestData = {
        cartItems: [{
          id: product.id,
          name: product.name,
          price: product.price,
          category: product.category,
          quantity: 1,
          variant: { price_override: null }
        }]
      };
      
      console.log('📤 ProductDetail: Birleşik API isteği gönderiliyor:', requestData);
      
      // Önce yeni birleşik API'yi dene
      const response = await api.post('/api/campaigns/check-unified-category-discounts', requestData);

      console.log('📦 ProductDetail: Birleşik kategori indirimi API yanıtı:', response);

      if (response.success && response.data.hasDiscounts && response.data.applicableDiscounts.length > 0) {
        const discount = response.data.applicableDiscounts[0];
        console.log('✅ ProductDetail: Kategori indirimi bulundu:', discount, 'Method:', response.data.method);
        
        // AffectedItems'den ilk ürünün indirim bilgisini al
        const affectedItem = discount.affectedItems && discount.affectedItems.length > 0 
          ? discount.affectedItems[0] 
          : null;
          
        setCategoryDiscount({
          campaignName: discount.campaignName,
          badgeText: discount.badgeText,
          badgeColor: discount.badgeColor,
          discountAmount: affectedItem ? affectedItem.discountAmount : discount.totalCampaignDiscount,
          discountType: discount.discountType,
          discountValue: discount.discountValue,
          method: response.data.method
        });
      } else {
        console.log('❌ ProductDetail: Kategori indirimi bulunamadı:', {
          productName: product.name,
          category: product.category,
          hasDiscounts: response.data?.hasDiscounts,
          discountsLength: response.data?.applicableDiscounts?.length,
          method: response.data?.method
        });
        setCategoryDiscount(null);
      }
    } catch (error) {
      console.error('❌ ProductDetail: Birleşik kategori indirimi kontrol hatası:', error);
      
      // Fallback: Eski simple API'yi dene
      console.log('🔄 ProductDetail: Fallback - Eski simple API deneniyor...');
      try {
        const fallbackResponse = await api.post('/api/campaigns/check-simple-category-discounts', {
          cartItems: [{
            id: product.id,
            name: product.name,
            price: product.price,
            category: product.category,
            quantity: 1,
            variant: { price_override: null }
          }]
        });

        if (fallbackResponse.success && fallbackResponse.data.hasDiscounts) {
          const discount = fallbackResponse.data.applicableDiscounts[0];
          console.log('✅ ProductDetail: Fallback ile kategori indirimi bulundu:', discount);
          setCategoryDiscount({
            campaignName: discount.campaignName,
            badgeText: discount.badgeText,
            badgeColor: discount.badgeColor,
            discountAmount: discount.totalCampaignDiscount,
            discountType: discount.discountType,
            discountValue: discount.discountValue,
            method: 'fallback_simple'
          });
        } else {
          setCategoryDiscount(null);
        }
      } catch (fallbackError) {
        console.error('❌ ProductDetail: Fallback API de başarısız:', fallbackError);
        setCategoryDiscount(null);
      }
    } finally {
      setCategoryDiscountLoading(false);
    }
  };

  // Flash Sale kontrolü
  const checkFlashSaleDiscount = async () => {
    if (!product || !product.id) {
      console.log('❌ ProductDetail: Flash Sale için ürün bilgisi eksik');
      return;
    }
    
    console.log('⚡ ProductDetail: Flash Sale kontrol ediliyor:', product.name);
    setFlashSaleLoading(true);
    
    try {
      const response = await api.get('/api/flash-sale/active');
      
      if (response.success && response.data && response.data.length > 0) {
        // Bu ürünün flash sale'de olup olmadığını kontrol et
        const arr = Array.isArray(response.data) ? response.data : (Array.isArray(response) ? response : []);
        const productFlashSale = arr.find(flashSale => 
          flashSale.products && flashSale.products.some(p => String(p.id) === String(product.id))
        );
        
        if (productFlashSale) {
          const productInSale = productFlashSale.products.find(p => p.id === product.id);
          const discountValue = productInSale.custom_discount_value || productFlashSale.discount_value;
          
          let discountAmount = 0;
          if (productFlashSale.discount_type === 'percentage') {
            discountAmount = (product.price * discountValue) / 100;
          } else {
            discountAmount = discountValue;
          }
          
          console.log('✅ ProductDetail: Flash Sale bulundu:', {
            campaignName: productFlashSale.name,
            discountValue,
            discountAmount
          });
          
          setFlashSaleDiscount({
            campaignName: productFlashSale.name,
            badgeText: productFlashSale.badge_text || `FLASH %${discountValue}`,
            badgeColor: productFlashSale.badge_color || '#ff4444',
            discountAmount: discountAmount,
            discountType: productFlashSale.discount_type,
            discountValue: discountValue,
            endDate: productFlashSale.end_date
          });
        } else {
          console.log('❌ ProductDetail: Bu ürün Flash Sale\'de değil');
          setFlashSaleDiscount(null);
        }
      } else {
        console.log('❌ ProductDetail: Aktif Flash Sale bulunamadı');
        setFlashSaleDiscount(null);
      }
    } catch (error) {
      console.error('❌ ProductDetail: Flash Sale kontrol hatası:', error);
      setFlashSaleDiscount(null);
    }
    
    setFlashSaleLoading(false);
  };
  

  useEffect(() => {
    const fetchProductDetail = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await api(`/api/products/${id}`);
        setProduct(data);
        
        if (data && data.variants && data.variants.length > 0) {
          const firstAvailableVariant = data.variants.find(v => v.stock > 0) || data.variants[0];
          setSelectedVariant(firstAvailableVariant);
        }

        // İlgili ürünleri getir (aynı kategorideki diğer ürünler)
        if (data && data.categories && data.categories.length > 0) {
          try {
            const categorySlug = data.categories[0].slug;
            const relatedResponse = await api.get(`/api/categories/${categorySlug}/products`);
            const filteredRelated = relatedResponse.data.filter(p => p.id !== id).slice(0, 4);
            setRelatedProducts(filteredRelated);
          } catch (error) {
            console.error('İlgili ürünler yüklenirken hata:', error);
          }
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchProductDetail();
  }, [id]);

  // Paket kampanyalarını kontrol et
  const checkPackageCampaigns = async () => {
    if (!product || !product.id) return;
    
    setPackageCampaignLoading(true);
    try {
      const response = await api.get('/api/campaigns/active-package-campaigns');
      
      if (response.success && response.data && Array.isArray(response.data) && response.data.length > 0) {
        const activeCampaigns = response.data.filter(campaign => {
          const now = new Date();
          const startDate = new Date(campaign.start_date);
          const endDate = new Date(campaign.end_date);
          
          return campaign.status === 'active' && 
                 now >= startDate && 
                 now <= endDate;
        });
        
        if (activeCampaigns.length > 0) {
          const activeCampaign = activeCampaigns[0];
          console.log('📦 ProductDetail: Aktif paket kampanyası bulundu:', activeCampaign.name);
          
          setPackageCampaign({
            id: activeCampaign.id,
            name: activeCampaign.name,
            description: activeCampaign.description,
            packageBuyCount: activeCampaign.package_buy_count,
            packagePayCount: activeCampaign.package_pay_count,
            packageType: activeCampaign.package_type,
            badgeText: activeCampaign.badge_text || 'PAKET KAMPANYASI',
            badgeColor: activeCampaign.badge_color || '#8b5cf6'
          });
        } else {
          console.log('📦 ProductDetail: Aktif paket kampanyası bulunamadı');
          setPackageCampaign(null);
        }
      } else {
        setPackageCampaign(null);
      }
    } catch (error) {
      console.error('❌ ProductDetail: Paket kampanyası kontrol hatası:', error);
      setPackageCampaign(null);
    }
    
    setPackageCampaignLoading(false);
  };

  // Özel gün kampanyalarını kontrol et
  const checkSpecialDayDiscounts = async () => {
    if (!product || !product.id) return;
    
    setSpecialDayLoading(true);
    try {
      const response = await api.post('/api/campaigns/check-special-day-discounts', {
        cartItems: [{
          id: product.id,
          name: product.name,
          price: product.price,
          category: product.category,
          quantity: 1,
          variant: { price_override: null }
        }],
        cartTotal: product.price,
        userInfo: null
      });

      if (response.success && response.data.hasDiscounts && response.data.applicableDiscounts.length > 0) {
        const discount = response.data.applicableDiscounts[0];
        console.log('🎊 ProductDetail: Özel gün kampanyası bulundu:', discount);
        setSpecialDayDiscount({
          campaignName: discount.campaignName,
          badgeText: discount.badgeText,
          badgeColor: discount.badgeColor,
          specialDayType: discount.specialDayType,
          discountAmount: discount.discountAmount,
          targetAudience: discount.targetAudience
        });
      } else {
        setSpecialDayDiscount(null);
      }
    } catch (error) {
      console.error('❌ ProductDetail: Özel gün kampanyası kontrol hatası:', error);
      setSpecialDayDiscount(null);
    }
    
    setSpecialDayLoading(false);
  };

  // Sepet kampanyalarını kontrol et
  useEffect(() => {
    const checkCartCampaigns = async () => {
      if (!product || !product.id) return;
      
      try {
        const response = await api.get('/api/campaigns/active-cart-campaigns');
        
        // API'den başarılı yanıt geldi ve aktif kampanya var mı kontrol et
        if (response.success && response.data && Array.isArray(response.data) && response.data.length > 0) {
          // Sadece gerçekten aktif olan kampanyaları filtrele
          const activeCampaigns = response.data.filter(campaign => {
            const now = new Date();
            const startDate = new Date(campaign.start_date);
            const endDate = new Date(campaign.end_date);
            
            return campaign.status === 'active' && 
                   now >= startDate && 
                   now <= endDate;
          });
          
          if (activeCampaigns.length > 0) {
            const activeCampaign = activeCampaigns[0];
            console.log('🛒 ProductDetail: Aktif sepet kampanyası bulundu:', activeCampaign.name);
            
            setCartCampaign({
              id: activeCampaign.id,
              name: activeCampaign.name,
              description: activeCampaign.description,
              discountType: activeCampaign.discount_type,
              discountValue: activeCampaign.discount_value,
              minCartAmount: activeCampaign.min_cart_amount,
              maxCartAmount: activeCampaign.max_cart_amount,
              badgeText: activeCampaign.badge_text || 'SEPET İNDİRİMİ',
              badgeColor: activeCampaign.badge_color || '#10b981'
            });
          } else {
            console.log('🛒 ProductDetail: Aktif sepet kampanyası bulunamadı');
            setCartCampaign(null);
          }
        } else {
          setCartCampaign(null);
        }
      } catch (error) {
        console.error('❌ ProductDetail: Sepet kampanyası kontrol hatası:', error);
        setCartCampaign(null);
      }
    };

    if (product && product.id) {
      checkCartCampaigns();
    }
  }, [product]);

  // Ürün yüklendikten sonra kategori indirimi kontrol et ve GA4 view_item eventi gönder
  useEffect(() => {
    if (product && product.id) {
      checkCategoryDiscount();
      checkFlashSaleDiscount();
      checkPackageCampaigns();
      checkSpecialDayDiscounts(); // Özel gün kampanyası kontrolü eklendi
      
      // GA4: Ürün görüntüleme eventi
      trackViewItem({
        id: product.id,
        name: product.name,
        price: product.price,
        final_price: product.price,
        category_name: product.category || (product.categories && product.categories.length > 0 ? product.categories[0].name : ''),
        discount_amount: product.discount_amount || 0
      });
    }
  }, [product]);

  const handleVariantSelect = (variant) => {
    setSelectedVariant(variant);
    if (variant && variant.variant_image_url) {
        const imageIndex = uniqueImages.indexOf(variant.variant_image_url);
        if (imageIndex !== -1) {
            goToIndex(imageIndex);
        }
    }
  };

  const handleAddToCart = () => {
    if (!selectedVariant) {
      toast.warn('Lütfen bir seçenek (beden/renk) belirleyin.');
      return;
    }
    if (selectedVariant.stock === 0) {
        toast.error("Seçtiğiniz ürün stokta bulunmamaktadır.");
        return;
    }
    
    // GA4: Sepete ekleme eventi
    trackAddToCart(
      {
        id: product.id,
        name: product.name,
        price: finalPrice > 0 ? finalPrice : product.price,
        final_price: finalPrice > 0 ? finalPrice : product.price,
        category_name: product.category || (product.categories && product.categories.length > 0 ? product.categories[0].name : ''),
        discount_amount: product.discount_amount || 0,
        selected_size: selectedVariant.size || selectedVariant.color || ''
      },
      1,
      selectedVariant.size || selectedVariant.color || ''
    );
    
    addToCart(product, selectedVariant, 1);
  };
  
  const handleThumbnailClick = (index) => {
    goToIndex(index);
  };

  const handlePrevClick = () => {
    const newIndex = (currentIndex - 1 + uniqueImages.length) % uniqueImages.length;
    goToIndex(newIndex);
  };

  const handleNextClick = () => {
    const newIndex = (currentIndex + 1) % uniqueImages.length;
    goToIndex(newIndex);
  };

  if (loading) return <div className='text-center p-12 text-lg'>Ürün Detayları Yükleniyor...</div>;
  if (error) return <div className='text-center text-red-500 p-12'>Hata: {error}</div>;
  if (!product) return <div className='text-center p-12'>Ürün bulunamadı.</div>;

  const toNum = (v) => {
    const n = typeof v === 'string' ? parseFloat(v.replace(',', '.')) : Number(v);
    return isNaN(n) ? 0 : n;
  };

  let originalPrice = toNum(product.original_price);
  let currentPrice = toNum(product.price);

  if (!originalPrice && toNum(product.discount_percentage) > 0) {
    originalPrice = currentPrice / (1 - toNum(product.discount_percentage) / 100);
  }

  if (!originalPrice) {
    originalPrice = currentPrice;
  }

  const categoryDiscountAmount = toNum(categoryDiscount?.discountAmount);
  const flashSaleDiscountAmount = toNum(flashSaleDiscount?.discountAmount);
  const specialDayDiscountAmount = toNum(specialDayDiscount?.discountAmount);

  const cartDiscountAmount = cartCampaign ? (() => {
    const discountValue = toNum(cartCampaign.discountValue || cartCampaign.discount_value);
    const discountType = cartCampaign.discountType || cartCampaign.discount_type || 'percentage';

    if (discountType === 'percentage') {
      return (currentPrice * discountValue) / 100;
    } else {
      return Math.min(discountValue, currentPrice);
    }
  })() : 0;

  const finalPrice = Math.max(0, currentPrice - categoryDiscountAmount - flashSaleDiscountAmount - cartDiscountAmount - specialDayDiscountAmount);

  const hasStock = selectedVariant ? selectedVariant.stock > 0 : product.variants.some(v => v.stock > 0);

  // Product Schema.org JSON-LD
  const productSchema = {
    "@type": "Product",
    "name": product.name,
    "image": uniqueImages.map(img => img.startsWith('http') ? img : `https://minelakids.com${img}`),
    "description": product.description || `${product.name} - Minela Kids kaliteli çocuk giyim ürünü`,
    "sku": product.sku || product.id,
    "mpn": product.mpn || product.id,
    "brand": {
      "@type": "Brand",
      "name": "Minela Kids"
    },
    "category": product.category || "Çocuk Giyim",
    "offers": {
      "@type": "Offer",
      "url": `https://minelakids.com/products/${product.id}`,
      "priceCurrency": "TRY",
      "price": finalPrice.toFixed(2),
      "availability": hasStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "seller": {
        "@type": "Organization",
        "name": "Minela Kids"
      }
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": product.average_rating || "4.8",
      "reviewCount": product.review_count || "50"
    }
  };

  // BreadcrumbList Schema
  const breadcrumbSchema = {
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Ana Sayfa",
        "item": "https://minelakids.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": product.categories && product.categories.length > 0 ? product.categories[0].name : "Kategoriler",
        "item": `https://minelakids.com/categories/${product.categories && product.categories.length > 0 ? product.categories[0].slug : ''}`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": product.name,
        "item": `https://minelakids.com/products/${product.id}`
      }
    ]
  };

  const questions = [
    {
      question: "Bu ürünün kumaşı nedir?",
      answer: "Ürünlerimiz genellikle %100 pamuklu kumaştan üretilmektedir. Detaylı bilgi için ürün açıklamalarını inceleyebilirsiniz."
    },
    {
      question: "Yıkama talimatları nelerdir?",
      answer: "Ürünlerimizi 30 derecede, hassas programda yıkamanızı ve ağartıcı kullanmamanızı öneririz."
    },
    {
      question: "Beden seçimi konusunda nasıl emin olabilirim?",
      answer: "Sayfamızda bulunan beden tablosunu inceleyerek çocuğunuz için en uygun bedeni seçebilirsiniz."
    }
  ];

  // FAQPage Schema.org structured data
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": questions.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  // Combine schemas using @graph
  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [
      productSchema,
      breadcrumbSchema,
      faqSchema
    ]
  };

  const productTitle = `${product.name} - Minela Kids | ${product.category || 'Çocuk Giyim'}`;
  const productDescription = product.description
    ? `${product.description.substring(0, 150)}...`
    : `${product.name} - Minela Kids kaliteli çocuk giyim ürünü. ${finalPrice ? `İndirimli fiyat: ${finalPrice.toFixed(2)}₺` : `Fiyat: ${product.price}₺`}`;

  return (
    <div>
      <SEOHead
        title={productTitle}
        description={productDescription}
        keywords={`${product.name}, çocuk giyim, ${product.category}, minela kids, çocuk kıyafeti, ${product.gender === 'kız' ? 'kız çocuk' : product.gender === 'erkek' ? 'erkek çocuk' : 'unisex'}`}
        ogType="product"
        ogImage={uniqueImages[0]}
        structuredData={combinedSchema}
        canonicalUrl={`https://minelakids.com/products/${product.id}`}
      />
    <div className="container mx-auto px-2 sm:px-4 py-4 max-w-full overflow-x-hidden">
      {/* Structured Data - Directly in body for better crawling */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(combinedSchema, null, 2) }}
      />
      
      {/* Breadcrumb Navigation */}
      <nav className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6 overflow-x-auto whitespace-nowrap">
        <Link to="/" className="flex items-center hover:text-brand-green transition-colors">
          <HomeIcon className="h-4 w-4 mr-1" />
          Ana Sayfa
        </Link>
        <span>›</span>
        <Link to="/categories" className="hover:text-brand-green transition-colors">
          Kategoriler
        </Link>
        {product.categories && product.categories.length > 0 && (
          <>
            <span>›</span>
            <Link 
              to={`/categories/${product.categories[0].slug}`}
              className="hover:text-brand-green transition-colors"
            >
              {product.categories[0].name}
            </Link>
          </>
        )}
        <span>›</span>
        <span className="text-gray-900 font-medium">{product.name}</span>
      </nav>

      <div className="bg-white rounded-xl shadow-lg p-3 sm:p-6 md:p-8 md:flex md:items-start md:space-x-12">
        <div className="md:w-1/2">
          
          <div className="relative w-full aspect-square overflow-hidden rounded-lg border">
              <Lens
                zoomFactor={2.5}
                lensSize={200}
                isStatic={false}
                ariaLabel="Ürün görseli yakınlaştırma"
              >
                <div 
                    className="flex transition-transform duration-1000 ease-in-out h-full"
                    style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                >
                    {uniqueImages.map((imgUrl, index) => (
                        <div key={index} className="w-full h-full flex-shrink-0">
                            <img 
                                src={imgUrl} 
                                alt={`${product.name} - ${index + 1}`}
                                className="w-full h-full object-cover"
                            />
                        </div>
                    ))}
                </div>
              </Lens>

              {uniqueImages.length > 1 && (
                  <>
                      <button onClick={handlePrevClick} className="absolute top-1/2 left-2 -translate-y-1/2 bg-white/60 p-2 rounded-full text-gray-800 hover:bg-white transition z-10"><ChevronLeftIcon className="w-6 h-6" /></button>
                      <button onClick={handleNextClick} className="absolute top-1/2 right-2 -translate-y-1/2 bg-white/60 p-2 rounded-full text-gray-800 hover:bg-white transition z-10"><ChevronRightIcon className="w-6 h-6" /></button>
                  </>
              )}
          </div>
          
          <div className="flex flex-wrap gap-2 justify-center mt-4">
            {uniqueImages.map((imgUrl, index) => (
              <button 
                key={index} 
                onClick={() => handleThumbnailClick(index)}
                className={`w-20 h-20 rounded-md overflow-hidden border-2 transition-all duration-200 ${currentIndex === index ? 'border-brand-green ring-2 ring-brand-green-light' : 'border-transparent hover:border-gray-400'}`}
              >
                <img src={imgUrl} alt={`Ürün görseli ${index + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
        
        <div className="md:w-1/2 mt-6 md:mt-0 overflow-hidden">
          {/* Bu kısımda değişiklik yok... */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-text-dark mb-3 sm:mb-4 break-words">{product.name}</h1>
          {product.description && (
            <p className="text-text-light text-sm sm:text-base md:text-lg mb-4 sm:mb-6 leading-relaxed break-words">
              {product.description}
            </p>
          )}
          <div className="mb-6">
            {/* Fiyat ve İndirim Gösterimi */}
            {(() => {
              const toNum = (v) => {
                const n = typeof v === 'string' ? parseFloat(v.replace(',', '.')) : Number(v);
                return isNaN(n) ? 0 : n;
              };
              
              // Orijinal fiyat ve mevcut fiyat hesaplama
              let originalPrice = toNum(product.original_price);
              let currentPrice = toNum(product.price);
              
              // Eğer original_price yoksa ve discount_percentage varsa, tersine hesapla
              if (!originalPrice && toNum(product.discount_percentage) > 0) {
                originalPrice = currentPrice / (1 - toNum(product.discount_percentage) / 100);
              }
              
              // Eğer hala original_price yoksa, price'ı kullan
              if (!originalPrice) {
                originalPrice = currentPrice;
              }
              
              const categoryDiscountAmount = toNum(categoryDiscount?.discountAmount);
              const flashSaleDiscountAmount = toNum(flashSaleDiscount?.discountAmount);
              const specialDayDiscountAmount = toNum(specialDayDiscount?.discountAmount);
              
              // Sepet kampanyası indirimi hesapla
              const cartDiscountAmount = cartCampaign ? (() => {
                const discountValue = toNum(cartCampaign.discountValue || cartCampaign.discount_value);
                const discountType = cartCampaign.discountType || cartCampaign.discount_type || 'percentage';
                
                if (discountType === 'percentage') {
                  return (currentPrice * discountValue) / 100;
                } else {
                  return Math.min(discountValue, currentPrice);
                }
              })() : 0;
              
              // Final fiyat hesaplama - tüm kampanyalar dahil
              const finalPrice = Math.max(0, currentPrice - categoryDiscountAmount - flashSaleDiscountAmount - cartDiscountAmount - specialDayDiscountAmount);
              
              const hasProductDiscount = (toNum(product.discount_percentage) > 0) || (originalPrice > currentPrice);
              const hasCategoryDiscount = categoryDiscount && categoryDiscountAmount > 0;
              const hasFlashSaleDiscount = flashSaleDiscount && flashSaleDiscountAmount > 0;
              const hasCartDiscount = cartCampaign && cartDiscountAmount > 0;
              const hasSpecialDayDiscount = specialDayDiscount && specialDayDiscountAmount > 0;
              const hasPackageCampaign = packageCampaign && packageCampaign.packageBuyCount && packageCampaign.packagePayCount;
              
              // Paket kampanyası potansiyel tasarrufu hesapla
              const packageSavings = hasPackageCampaign ? (() => {
                const buyCount = packageCampaign.packageBuyCount;
                const payCount = packageCampaign.packagePayCount;
                const saveCount = buyCount - payCount;
                return (currentPrice * saveCount) / buyCount; // Ortalama tasarruf
              })() : 0;
              
              // Toplam tasarruf = orijinal fiyat - final fiyat + sepet kampanyası potansiyeli + paket kampanyası potansiyeli + özel gün kampanyası
              const totalSavings = Math.max(0, originalPrice - finalPrice) + cartDiscountAmount + packageSavings + specialDayDiscountAmount;

              // Debug logları
              console.log('💰 ProductDetail: Fiyat hesaplama debug:', {
                originalPrice,
                currentPrice,
                categoryDiscount,
                categoryDiscountAmount,
                flashSaleDiscountAmount,
                cartCampaign,
                cartDiscountAmount,
                specialDayDiscount,
                specialDayDiscountAmount,
                finalPrice,
                hasProductDiscount,
                hasCategoryDiscount,
                hasFlashSaleDiscount,
                hasCartDiscount,
                hasSpecialDayDiscount,
                totalSavings
              });

              return (
                <div className="flex flex-col space-y-4">
                  {/* İndirim Rozetleri */}
                  <div className="flex flex-wrap gap-2">
                    {hasProductDiscount && (
                      <div className="inline-flex items-center bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                        %{Math.round(product.discount_percentage)} ÜRÜN İNDİRİMİ
                      </div>
                    )}
                    {hasCategoryDiscount && (
                      <div 
                        className="inline-flex items-center text-white px-3 py-1 rounded-full text-sm font-bold"
                        style={{ backgroundColor: categoryDiscount.badgeColor || '#8b5cf6' }}
                      >
                        {categoryDiscount.badgeText || 'KAMPANYA'}
                      </div>
                    )}
                    {hasFlashSaleDiscount && (
                      <div 
                        className="inline-flex items-center text-white px-3 py-1 rounded-full text-sm font-bold animate-pulse"
                        style={{ backgroundColor: flashSaleDiscount.badgeColor || '#ff4444' }}
                      >
                        ⚡ {flashSaleDiscount.badgeText || 'FLASH SALE'}
                      </div>
                    )}
                    {hasCartDiscount && (
                      <div 
                        className="inline-flex items-center text-white px-3 py-1 rounded-full text-sm font-bold"
                        style={{ backgroundColor: cartCampaign.badgeColor || '#10b981' }}
                      >
                        🛒 {cartCampaign.badgeText || 'SEPET İNDİRİMİ'}
                      </div>
                    )}
                    {hasSpecialDayDiscount && (
                      <div 
                        className="inline-flex items-center text-white px-3 py-1 rounded-full text-sm font-bold"
                        style={{ backgroundColor: specialDayDiscount.badgeColor || '#f59e0b' }}
                      >
                        🎊 {specialDayDiscount.badgeText || 'ÖZEL GÜN'}
                      </div>
                    )}
                    {hasPackageCampaign && (
                      <div 
                        className="inline-flex items-center text-white px-3 py-1 rounded-full text-sm font-bold"
                        style={{ backgroundColor: packageCampaign.badgeColor || '#8b5cf6' }}
                      >
                        📦 {packageCampaign.badgeText || 'PAKET KAMPANYASI'}
                      </div>
                    )}
                  </div>
                  
                  {/* Fiyatlar - Geliştirilmiş Tasarım */}
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-3 sm:p-4 md:p-6 mb-3 sm:mb-4">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6 mb-3 sm:mb-4">
                      <div className="flex-1 w-full">
                        <div className="text-red-500 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-2">
                          ₺{finalPrice.toFixed(2)}
                        </div>
                        {(hasProductDiscount || hasCategoryDiscount || hasCartDiscount) && (
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                            <span className="text-gray-500 text-lg sm:text-xl md:text-2xl line-through">
                              ₺{originalPrice.toFixed(2)}
                            </span>
                            <div className="bg-red-500 text-white px-3 py-1 sm:px-4 sm:py-2 rounded-full font-bold text-sm sm:text-base md:text-lg">
                              %{Math.round(((originalPrice - finalPrice) / originalPrice) * 100)} İNDİRİM
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Tasarruf Özeti */}
                      <div className="w-full sm:w-auto text-left sm:text-right">
                        <div className="bg-green-500 text-white rounded-xl p-3 sm:p-4">
                          <div className="text-white text-xs sm:text-sm font-medium opacity-90">TOPLAM TASARRUF</div>
                          <div className="text-white text-2xl sm:text-2xl md:text-3xl font-black break-words">₺{totalSavings.toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* İndirim Detayları */}
                  {(hasProductDiscount || hasCategoryDiscount || hasFlashSaleDiscount || hasCartDiscount) && (
                    <div className="space-y-2 overflow-x-hidden">
                      {hasProductDiscount && (
                        <div className="bg-red-100 text-red-800 px-3 sm:px-4 py-2 rounded-lg inline-block max-w-full">
                          <span className="font-bold text-xs sm:text-sm break-words">
                            Ürün İndirimi: ₺{(originalPrice - currentPrice).toFixed(2)}
                          </span>
                        </div>
                      )}
                      {hasCategoryDiscount && (
                        <div className="bg-purple-100 text-purple-800 px-3 sm:px-4 py-2 rounded-lg inline-block max-w-full">
                          <span className="font-bold text-xs sm:text-sm break-words">
                            {categoryDiscount.campaignName}: ₺{categoryDiscountAmount.toFixed(2)}
                          </span>
                        </div>
                      )}
                      {hasFlashSaleDiscount && (
                        <div className="bg-red-100 text-red-800 px-3 sm:px-4 py-2 rounded-lg inline-block max-w-full">
                          <span className="font-bold text-xs sm:text-sm break-words">
                            ⚡ {flashSaleDiscount.campaignName}: ₺{flashSaleDiscountAmount.toFixed(2)}
                          </span>
                        </div>
                      )}
                      {hasCartDiscount && (
                        <div className="bg-emerald-100 text-emerald-800 px-3 sm:px-4 py-2 rounded-lg inline-block max-w-full">
                          <span className="font-bold text-xs sm:text-sm break-words">
                            🛒 Sepet İndirimi: ₺{cartDiscountAmount.toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="bg-green-100 text-green-800 px-3 sm:px-4 py-2 rounded-lg inline-block max-w-full">
                        <span className="font-bold text-sm sm:text-base md:text-lg break-words">
                          Toplam Tasarruf: ₺{totalSavings.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Paket Kampanyası Mesajı */}
                  {hasPackageCampaign && (
                    <div className="bg-gradient-to-r from-purple-100 to-indigo-100 border-2 border-purple-300 rounded-xl p-3 sm:p-4 mt-3 sm:mt-4 max-w-full overflow-hidden">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-bold">📦</span>
                        </div>
                        <div className="font-bold text-purple-800">
                          {packageCampaign.name}
                        </div>
                      </div>
                      <div className="text-purple-700 text-sm">
                        <div className="font-semibold mb-1">
                          {packageCampaign.packageBuyCount} al {packageCampaign.packagePayCount} öde kampanyası!
                        </div>
                        <div className="text-purple-600">
                          Sepete bir ürün daha ekleyin ve {packageCampaign.packageBuyCount - packageCampaign.packagePayCount} ürün bedava alın!
                        </div>
                        <div className="mt-2 text-purple-800 font-bold">
                          Potansiyel tasarruf: ₺{packageSavings.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Sepet Kampanyası Bilgisi - Sadece API'den gelirse görüntüle */}
          {cartCampaign && cartCampaign.id && (
            <div className="bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 border-2 border-emerald-300 rounded-2xl p-3 sm:p-4 md:p-6 mb-4 sm:mb-6 shadow-xl hover:shadow-2xl transition-all duration-300 max-w-full overflow-hidden">
              {/* Başlık Kısmı */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-3 sm:gap-4 mb-3 sm:mb-4">
                <div className="flex items-center gap-2 sm:gap-4">
                  <div 
                    className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-2xl flex items-center justify-center text-white font-bold shadow-lg flex-shrink-0"
                    style={{ background: `linear-gradient(135deg, ${cartCampaign.badgeColor || '#10b981'}, #059669)` }}
                  >
                    <span className="text-lg sm:text-xl md:text-2xl">🛒</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-emerald-800 font-black text-sm sm:text-base md:text-lg break-words">
                      {cartCampaign.badgeText || 'SEPET İNDİRİMİ'}
                    </div>
                    <div className="text-emerald-700 text-xs sm:text-sm font-semibold break-words">
                      {cartCampaign.name}
                    </div>
                    <div className="text-emerald-600 text-xs break-words">
                      {cartCampaign.description}
                    </div>
                  </div>
                </div>
                <div className="w-full sm:w-auto text-left sm:text-right">
                  <div className="bg-gradient-to-r from-emerald-600 to-green-600 text-white px-3 sm:px-4 py-2 rounded-xl font-black text-base sm:text-lg md:text-xl shadow-lg">
                    {cartCampaign.discountType === 'percentage' ? `%${cartCampaign.discountValue}` : `${cartCampaign.discountValue} TL`}
                  </div>
                  <div className="text-emerald-600 text-xs mt-1 font-medium">
                    İNDİRİM ORANI
                  </div>
                </div>
              </div>
              
              {/* Detaylı Hesaplama Kısmı */}
              {cartCampaign.minCartAmount && (
                <div className="bg-white bg-opacity-90 rounded-xl p-3 sm:p-4 md:p-5 border border-emerald-200 shadow-inner overflow-hidden">
                  <div className="flex items-center gap-2 mb-3 sm:mb-4">
                    <span className="text-xl sm:text-2xl flex-shrink-0">📊</span>
                    <div className="text-emerald-800 font-bold text-sm sm:text-base md:text-lg break-words">
                      Detaylı Tasarruf Hesaplaması
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Sol Kolon - Mevcut Durum */}
                    <div className="space-y-3">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="text-gray-600 text-sm font-medium mb-2">🏷️ Mevcut Ürün Bilgileri:</div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Orijinal fiyat:</span>
                            <span className="font-medium">{product.price} TL</span>
                          </div>
                          
                          {(() => {
                            const currentPrice = selectedVariant?.price_override || product.price;
                            const productDiscount = product.price - currentPrice;
                            
                            return (
                              <>
                                {productDiscount > 0 && (
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Ürün indirimi:</span>
                                    <span className="text-red-600 font-medium">-{productDiscount.toFixed(2)} TL</span>
                                  </div>
                                )}
                                
                                <div className="flex justify-between border-t border-gray-300 pt-2">
                                  <span className="text-gray-800 font-semibold">Mevcut fiyat:</span>
                                  <span className="font-bold text-lg">{currentPrice} TL</span>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                    
                    {/* Sağ Kolon - Sepet Kampanyası */}
                    <div className="space-y-3">
                      <div className="bg-gradient-to-br from-emerald-100 to-green-100 rounded-lg p-3 border border-emerald-200">
                        <div className="text-emerald-700 text-sm font-bold mb-2">🛒 Sepet Kampanyası Avantajı:</div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-emerald-600">Minimum sepet:</span>
                            <span className="font-medium">{cartCampaign.minCartAmount} TL</span>
                          </div>
                          
                          <div className="flex justify-between">
                            <span className="text-emerald-600">İndirim oranı:</span>
                            <span className="font-medium">
                              {cartCampaign.discountType === 'percentage' ? `%${cartCampaign.discountValue}` : `${cartCampaign.discountValue} TL`}
                            </span>
                          </div>
                          
                          <div className="flex justify-between border-t border-emerald-300 pt-2">
                            <span className="text-emerald-700 font-semibold">Sepet indirimi:</span>
                            <span className="text-emerald-700 font-bold text-lg">
                              -{cartCampaign.discountType === 'percentage' 
                                ? ((cartCampaign.minCartAmount * cartCampaign.discountValue) / 100).toFixed(0)
                                : cartCampaign.discountValue} TL
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Toplam Tasarruf */}
                  <div className="mt-3 sm:mt-4 bg-gradient-to-r from-yellow-100 via-amber-100 to-orange-100 border-2 border-yellow-300 rounded-xl p-3 sm:p-4 overflow-hidden">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-3">
                      <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                        <span className="text-2xl sm:text-3xl flex-shrink-0">🎆</span>
                        <div className="min-w-0 flex-1">
                          <div className="text-amber-800 font-black text-base sm:text-lg md:text-xl break-words">
                            TOPLAM TASARRUF
                          </div>
                          <div className="text-amber-700 text-xs sm:text-sm font-medium break-words">
                            Bu ürünü {cartCampaign.minCartAmount} TL'lik sepete eklediğinizde
                          </div>
                        </div>
                      </div>
                      <div className="w-full sm:w-auto text-left sm:text-right">
                        <div className="text-amber-900 font-black text-2xl sm:text-2xl md:text-3xl break-words">
                          {(() => {
                            const currentPrice = selectedVariant?.price_override || product.price;
                            const productDiscount = product.price - currentPrice;
                            const cartDiscount = cartCampaign.discountType === 'percentage' 
                              ? (cartCampaign.minCartAmount * cartCampaign.discountValue) / 100
                              : cartCampaign.discountValue;
                            return `${(productDiscount + cartDiscount).toFixed(0)} TL`;
                          })()}
                        </div>
                        <div className="text-amber-700 text-xs font-medium">
                          KAZANCINIZ
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Call to Action */}
                  <div className="mt-3 sm:mt-4 text-center">
                    <div className="bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl px-4 sm:px-6 py-2 sm:py-3 inline-block max-w-full">
                      <div className="flex items-center gap-2">
                        <span className="text-base sm:text-lg md:text-xl flex-shrink-0">🎯</span>
                        <span className="font-bold text-xs sm:text-sm break-words">
                          Şimdi sepete ekleyin, {cartCampaign.minCartAmount} TL'ye tamamlayın ve indirimi kazanın!
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Bilgi Notu */}
                  <div className="mt-3 text-xs text-emerald-600 bg-emerald-50 rounded-lg px-3 py-2 border border-emerald-200">
                    ℹ️ <strong>Bilgi:</strong> Sepet kampanyası indirimi, toplam sepet tutarı {cartCampaign.minCartAmount} TL'ye ulaştığında otomatik olarak uygulanır.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Kategori Etiketleri */}
          {product.categories && product.categories.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-600 mb-2">Kategoriler:</h3>
              <div className="flex flex-wrap gap-2">
                {product.categories.map((category) => (
                  <Link
                    key={category.id}
                    to={`/categories/${category.slug}`}
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-brand-green/10 text-brand-green hover:bg-brand-green hover:text-white transition-colors duration-200"
                  >
                    {category.name}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {product.variants && product.variants.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Seçenekler:</h3>
              <div className="flex flex-wrap gap-3">
                {product.variants.map((variant, index) => (
                  <button
                    key={`${variant.color}-${variant.size}-${index}`}
                    type="button"
                    onClick={() => handleVariantSelect(variant)}
                    className={`p-3 border rounded-lg text-sm font-medium text-center transition-all duration-200
                      ${selectedVariant && selectedVariant.color === variant.color && selectedVariant.size === variant.size ? 'bg-brand-green text-white border-brand-green shadow-md ring-2 ring-offset-2 ring-brand-green-light' : 'bg-white text-gray-700 border-gray-300'}
                      ${variant.stock === 0 ? 'opacity-50 cursor-not-allowed bg-gray-100 text-gray-400 line-through' : 'hover:border-brand-green'}`}
                    disabled={variant.stock === 0}
                  >
                    {variant.color} - {variant.size}
                    {variant.stock === 0 && <span className='text-xs block'>(Tükendi)</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
          <button
            onClick={handleAddToCart}
            className="w-full mt-4 bg-brand-green text-white py-4 rounded-lg font-bold text-xl hover:bg-brand-green-light transition duration-300 shadow-md disabled:bg-gray-400 disabled:cursor-not-allowed"
            disabled={!selectedVariant || (selectedVariant && (selectedVariant.stock || 0) === 0)}
          >
            {!selectedVariant 
              ? 'Renk ve Beden Seçin' 
              : (selectedVariant.stock || 0) === 0 
                ? 'Stokta Yok' 
                : 'Sepete Ekle'
            }
          </button>
        </div>
      </div>

      {product.details && (
          <div className="bg-white rounded-xl shadow-lg p-8 mt-8">
              <h2 className="text-2xl font-bold text-text-dark border-b pb-3 mb-4">Ayrıntılar</h2>
              <div className="prose max-w-none prose-p:my-2 prose-ul:my-2 prose-headings:my-3">
                <div dangerouslySetInnerHTML={{ __html: product.details.replace(/\n/g, '<br />') }} />
              </div>
          </div>
      )}

      {/* İlgili Ürünler Bölümü */}
      {relatedProducts.length > 0 && (
        <div className="mt-16">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Aynı Kategoriden Diğer Ürünler
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <Link
                  key={relatedProduct.id}
                  to={`/products/${relatedProduct.id}`}
                  className="group block bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-shadow duration-200"
                >
                  <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-t-lg bg-gray-200">
                    <img
                      src={relatedProduct.main_image_url || '/placeholder-image.jpg'}
                      alt={relatedProduct.name}
                      className="h-48 w-full object-cover object-center group-hover:scale-105 transition-transform duration-200"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-medium text-gray-900 line-clamp-2">
                      {relatedProduct.name}
                    </h3>
                    <p className="mt-2 text-lg font-bold text-brand-green">
                      ₺{parseFloat(relatedProduct.price).toFixed(2)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Ürün Yorumları Bölümü */}
      <div className="mt-8">
        <ProductReviewSection 
          productId={product.id}
          productName={product.name}
        />
      </div>

      {/* SSS Bölümü */}
      <div className="bg-white rounded-xl shadow-lg p-8 mt-8">
        <h2 className="text-2xl font-bold text-text-dark border-b pb-3 mb-4">Sıkça Sorulan Sorular</h2>
        <div className="space-y-6">
          {questions.map((item, index) => (
            <div key={index}>
              <h3 className="text-lg font-semibold text-text-dark mb-2">{item.question}</h3>
              <p className="text-text-light leading-relaxed">{item.answer}</p>
            </div>
          ))}
        </div>
      </div>

    </div>
    </div>
  );
}

export default ProductDetail;
